import { useDateNow, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup } from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { CalendarApi } from '@fullcalendar/core';
import type { CalendarEventExtended, CustomAttributes } from 'shared-types';

interface Props {
  calendarApi?: CalendarApi;
  event: CalendarEventExtended;
  setEvent?: React.Dispatch<React.SetStateAction<CalendarEventExtended>>;
}

const CalendarEventParticipantAddSelf = ({
  calendarApi,
  event,
  setEvent
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const { language, messages, theme, user } = useReduxState();

  const handleJoin = async (values: CustomAttributes) => {
    const userAttributes = {};

    Object.keys(values).forEach((fieldKey) => {
      if (fieldKey.startsWith('userAttributes-')) {
        const attributeId = fieldKey.split('-')[1];
        userAttributes[attributeId] = values[fieldKey];
      }
    });

    const url =
      event.access === 'request-access'
        ? `/admin/calendar-events/${event.id}/request-access`
        : `/admin/calendar-events/${event.id}/registration`;

    const response = await api.post(url, {
      userAttributes,
      userId: user.id
    });

    if (response.data.status === 'full') {
      dispatch.addNotice({
        text: messages.calendarEventJoinFull,
        type: 'error'
      });
      return true;
    }

    const newEvent: CalendarEventExtended = {
      ...event,
      accessRequestId: response.data.requestId,
      reqUserCustomAttributes: { ...userAttributes },
      ...(response.data.status === 'queued'
        ? event.access === 'request-access'
          ? { userStatus: 'queued' }
          : {
              numberOfParticipantsQueued: event.numberOfParticipantsQueued + 1,
              participantQueueSpot: event.numberOfParticipantsQueued + 1,
              userStatus: 'queued'
            }
        : {
            isFull:
              event.participationLimit &&
              event.numberOfParticipantsAdded + 1 >= event.participationLimit,
            numberOfParticipantsAdded: event.numberOfParticipantsAdded + 1,
            userStatus: 'added'
          })
    };

    if (calendarApi) {
      const calendarEvent = calendarApi.getEventById(`${event.id}`);
      if (newEvent.userStatus === 'queued') {
        calendarEvent?.setExtendedProp('icon', ['far', 'clock']);
        calendarEvent?.setProp('color', theme.warningLightColor);
      } else {
        calendarEvent?.setExtendedProp('icon', 'check');
        calendarEvent?.setProp('color', theme.successLightColor);
      }
      calendarEvent?.setExtendedProp(
        'accessRequestId',
        response.data.requestId
      );
      calendarEvent?.setExtendedProp(
        'numberOfParticipantsAdded',
        newEvent.numberOfParticipantsAdded
      );
      calendarEvent?.setExtendedProp(
        'numberOfParticipantsQueued',
        newEvent.numberOfParticipantsQueued
      );
      calendarEvent?.setExtendedProp(
        'participantQueueSpot',
        newEvent.participantQueueSpot
      );
      calendarEvent?.setExtendedProp('reqUserCustomAttributes', {
        ...userAttributes
      });
      calendarEvent?.setExtendedProp('userStatus', newEvent.userStatus);
    }
    if (setEvent) setEvent(newEvent);

    dispatch.addNotice({
      text:
        newEvent.userStatus === 'queued'
          ? messages.calendarEventJoinSuccessQueue
          : messages.calendarEventJoinSuccess,
      type: 'success'
    });
    dispatch.updatePageState({ calendarEvent: newEvent });

    return true;
  };

  const eventAccessMessage =
    event.access === 'request-access'
      ? messages.courseAccessRequestActionMakeRequest
      : event.isFull && event.queueEnabled
      ? messages.eventCalendarJoinQueue
      : messages.eventCalendarJoinEvent;

  const eventHasStarted =
    Date.parse(event.lastJoinDate || event.startDate) <= dateNow;

  const isDisabled =
    eventHasStarted ||
    (event.isFull && !event.queueEnabled) ||
    event.isQueueFull;

  const tooltip = eventHasStarted
    ? messages.eventCalendarJoinDisabled
    : (event.isFull && !event.queueEnabled) || event.isQueueFull
    ? messages.eventCalendarIsFull
    : undefined;

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonDisabled={isDisabled}
      buttonProps={{ isRounded: true, tooltip }}
      buttonText={eventAccessMessage}
      initialValues={{}}
      modalTitle={eventAccessMessage}
      modalActionText={messages.commonConfirm}
      modalWidth="medium"
      onSubmit={handleJoin}
    >
      {() => (
        <div>
          <div className="confirm-text">
            {event.userAttributes?.length
              ? messages.eventCalendarFillInInformation
              : messages.eventCalendarJoinConfirm}
          </div>

          {event.userAttributes?.map((attr) => (
            <FormGroup
              key={attr.id}
              label={attr.titleTranslations?.[language] || attr.title}
              labelIsOptional={!attr.isRequired}
            >
              <ProductUserCustomAttributeRenderFormField attribute={attr} />
            </FormGroup>
          ))}

          <style jsx>{`
            .confirm-text:not(:last-child) {
              margin-bottom: 20px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarEventParticipantAddSelf;
